import { format, formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

// ----------------------------------------------------------------------

const getMonthName = (month) => {
  // Obtener el nombre del mes e
  switch (month) {
    case 0:
      month = "Enero";
      break;
    case 1:
      month = "Febrero";
      break;
    case 2:
      month = "Marzo";
      break;
    case 3:
      month = "Abril";
      break;
    case 4:
      month = "Mayo";
      break;
    case 5:
      month = "Junio";
      break;
    case 6:
      month = "Julio";
      break;
    case 7:
      month = "Agosto";
      break;
    case 8:
      month = "Septiembre";
      break;
    case 9:
      month = "Octubre";
      break;
    case 10:
      month = "Noviembre";
      break;
    case 11:
      month = "Diciembre";
      break;
    default:
      break;
  }

  return month;
};

export const getDayFromDate = (start, end) => {
  // Obtener una cadena fromada por el día de la semana y el número del día del mes
  // Ejemplo: "Lunes 1"
  let days = [
    "Domingo",
    "Lunes",
    "Martes",
    "Miércoles",
    "Jueves",
    "Viernes",
    "Sábado",
  ];
  let day = start.getDate();
  let dayNumber = start.getDate();
  let month = start.getMonth();
  let year = start.getFullYear();

  let startMonth = getMonthName(month);
  let endMonth = getMonthName(end.getMonth());

  if (start.getFullYear() !== end.getFullYear())
    return startMonth !== endMonth
      ? `${days[start.getDay()]} ${dayNumber} de ${getMonthName(
          month
        )} de ${year} - ${
          days[end.getDay()]
        } ${end.getDate()} de ${getMonthName(
          end.getMonth()
        )} de ${end.getFullYear()}`
      : `${days[start.getDay()]} ${dayNumber} de ${year} - ${
          days[end.getDay()]
        } ${end.getDate()} de ${getMonthName(
          end.getMonth()
        )} de ${end.getFullYear()}`;

  return startMonth !== endMonth
    ? `${days[start.getDay()]} ${dayNumber} de ${getMonthName(month)} - ${
        days[end.getDay()]
      } ${end.getDate()} de ${getMonthName(end.getMonth())}`
    : `${days[start.getDay()]} ${dayNumber} - ${
        days[end.getDay()]
      } ${end.getDate()} de ${getMonthName(end.getMonth())}`;
};

/**
 * 2026-10-20 · Etiqueta de un arqueo con su jornada en hora local:
 * «Lunes 20 - Martes 21 de Octubre · 03:00 → 03:00». El API manda Start/End
 * del arqueo = jornada de negocio en UTC («…Z»); sin zona se leen como UTC.
 */
export const getArqueoDayLabel = (start, end) => {
  const s = serverUtcDate(start);
  const e = serverUtcDate(end);
  if (isNaN(s) || isNaN(e)) return "";
  const hhmm = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${getDayFromDate(s, e)} · ${hhmm(s)} → ${hhmm(e)}`;
};

export function fDate(date) {
  return format(new Date(date), "yyyy MM dd HH:mm p");
}

export function fDateSimple(date) {
  return format(new Date(date), "dd MMMM yyyy", { locale: es });
}

// Este formato
/**
 * Marca de tiempo del servidor (UTC). El API serializa los DateTime UTC sin
 * zona ("2026-10-03T19:00:00"): new Date() los tomaría como hora local
 * (2 h de desfase en Madrid). Solo añade la "Z" si no trae zona; las que ya
 * vienen con "Z" u offset se respetan.
 */
const ISO_WITHOUT_ZONE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
export function serverUtcDate(value) {
  if (value instanceof Date) return value;
  if (value === null || value === undefined || value === "") return new Date(NaN);
  const s = String(value);
  return new Date(ISO_WITHOUT_ZONE.test(s) ? `${s}Z` : s);
}

// 2026-10-20 · Las fechas del servidor pueden venir ya con zona («…Z», p. ej.
// la jornada de los arqueos): añadir otra «Z» daba «…ZZ» → fecha inválida →
// se pintaba la hora ACTUAL. serverUtcDate solo la añade si no trae zona.
export function fDateTime(date) {
  try {
    return format(serverUtcDate(date), "dd MMM yyyy HH:mm");
  } catch (error) {
    return format(new Date(), "dd MMM yyyy HH:mm");
  }
}

export function fDateTimeZ(date) {
  try {
    return serverUtcDate(date);
  } catch (error) {
    return new Date();
  }
}

// Este formato
export function fDateTimeSimple(date) {
  try {
    return format(serverUtcDate(date), "dd/MM/yyyy");
  } catch (error) {
    return format(new Date(), "dd/MM/yyyy");
  }
}

export function fDateTimeSuffix(date) {
  try {
    return format(serverUtcDate(date), "dd/MM/yyyy p");
  } catch (error) {
    return format(new Date(), "dd/MM/yyyy p");
  }
}

export function fDateTimeSuffixJustDate(date) {
  try {
    return format(serverUtcDate(date), "dd/MM/yyyy HH:mm");
  } catch (error) {
    return format(new Date(), "dd/MM/yyyy HH:mm");
  }
}

export function fToNow(date) {
  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
    locale: es,
  });
}

/**
 * Convierte un TimeSpan serializado por .NET a segundos totales.
 * Maneja AMBOS formatos:
 *   - "hh:mm:ss" (cuando dura < 24h)
 *   - "d.hh:mm:ss" (cuando dura ≥ 24h — .NET cambia el formato)
 *
 * Antes este parsing hacía `split(":")[0]` directamente, lo que para un
 * turno de 25h ("1.01:00:00") daba `parseInt("1.01") = 1` y perdía las
 * 24h del prefijo "d." — la duración aparecía como 1h en vez de 25h.
 */
function timeSpanToSeconds(timeSpan) {
  if (!timeSpan || typeof timeSpan !== "string") return 0;
  let rest = timeSpan;
  let days = 0;
  // El "." sólo cuenta como separador de días si está ANTES del primer ":"
  // — fracciones de segundo (formato "01:00:00.5000000") no nos interesan.
  const colonIdx = rest.indexOf(":");
  const dotIdx = rest.indexOf(".");
  if (dotIdx >= 0 && colonIdx >= 0 && dotIdx < colonIdx) {
    days = parseInt(rest.substring(0, dotIdx)) || 0;
    rest = rest.substring(dotIdx + 1);
  }
  const [h = 0, m = 0, s = 0] = rest.split(":").map((p) => parseInt(p) || 0);
  return days * 86400 + h * 3600 + m * 60 + s;
}

export function fTimeSpanToText(timeSpan) {
  const totalSec = timeSpanToSeconds(timeSpan);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  return `${hours} horas ${minutes} minutos`;
}

export function sumTimeSpans(timeSpans) {
  const totalSeconds = timeSpans.reduce(
    (acc, time) => acc + timeSpanToSeconds(time),
    0,
  );

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    hours: hours,
    time: [hours, minutes, seconds]
      .map((n) => String(n).padStart(2, "0"))
      .join(":"),
  };
}

/**
 * Formats milliseconds to HH:MM:SS format
 */
export const formatElapsedTime = (milliseconds) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const padZero = (num) => num.toString().padStart(2, "0");

  if (hours > 0) {
    return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
  }

  return `${padZero(minutes)}:${padZero(seconds)}`;
};

/**
 * Calculates and formats the time difference between two dates
 */
export const formatTimeDifference = (startDate, endDate) => {
  const diffMs = endDate.getTime() - startDate.getTime();
  return formatElapsedTime(diffMs);
};

export const removeTimeZoneOffset = (date) => {
  // remove the Z at the end of the date string

  if (date.endsWith("Z")) {
    return date.slice(0, -1);
  }

  return date;
};

export const fDateTimeDate = (date) => {
  try {
    return format(serverUtcDate(date), "dd/MM/yyyy");
  } catch (error) {
    return format(new Date(), "dd/MM/yyyy");
  }
};
